import React from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { getFieldValues, getSanitizedValue } from '../../Helpers/Util'

const StyledTableContainer = styled(TableContainer)(() => ({
  borderRadius: '8px 8px 0 0',
  border: '1px solid #E0E0E0',
  '& .MuiTable-root': {
    backgroundColor: 'white',
    borderRadius: '8px',
  },
  '& .MuiTableCell-root': {
    borderBottom: '1px solid #e0e0e0',
    padding: '12px 16px',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    color: '#6A6C6D',
    fontWeight: 500,
    fontSize: '14px',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    color: '#333333',
    fontSize: '14px',
  },
}))

interface TableData {
  id: string
  [key: string]: any
}

interface HeaderInfo {
  name: string
  isRequired: boolean
}

interface ComplexFieldViewProps {
  data: any
}

export const ComplexFieldView: React.FC<ComplexFieldViewProps> = ({ data }) => {
  const generateHeadersFromData = (complexData: any): HeaderInfo[] => {
    if (!complexData?.length) {
      return []
    }

    const headerMap = new Map<string, boolean>()

    complexData.forEach((group: any) => {
      if (Array.isArray(group) && group.length > 0) {
        group.forEach((field: any) => {
          const fieldName = field?.parameterId?.parameterName || field?.fieldName || 'Unknown Field'
          const isRequired = field?.required?.toLowerCase() === 'yes'
          if (!headerMap.has(fieldName) || isRequired) {
            headerMap.set(fieldName, isRequired)
          }
        })
      } else if (group?.parameterId?.parameterName || group?.parameter?.parameterName) {
        const fieldName = group?.parameterId?.parameterName || group?.parameter?.parameterName
        const isRequired = group?.required?.toLowerCase() === 'yes'
        headerMap.set(fieldName, isRequired)
      }
    })

    return Array.from(headerMap.entries()).map(([fieldName, isRequired]) => ({
      name: fieldName,
      isRequired,
    }))
  }

  const finalHeaders = generateHeadersFromData(data)

  const transformDataToTable = (complexData: any): TableData[] => {
    const tableData: TableData[] = []

    if (!complexData?.length) {
      return tableData
    }

    complexData.forEach((group: any, groupIdx: number) => {
      if (!Array.isArray(group) || group.length === 0) {
        const fieldName =
          group?.parameterId?.parameterName || group?.parameter?.parameterName || 'Unknown Field'
        const fieldValue = getFieldValues(group.value, group?.parameterId?.fieldType) || 'NA'

        const rowData: TableData = {
          id: `${group?.parameterId?._id || groupIdx}`,
        }

        rowData[fieldName] = getSanitizedValue(fieldValue)

        tableData.push(rowData)
      } else {
        const rowData: TableData = {
          id: `group-${groupIdx}`,
        }

        group.forEach((field: any) => {
          const fieldName = field?.parameterId?.parameterName || field?.fieldName || 'Unknown Field'
          const fieldValue = getFieldValues(field.value, field?.parameterId?.fieldType)

          rowData[fieldName] = getSanitizedValue(fieldValue)
        })

        tableData.push(rowData)
      }
    })

    return tableData
  }

  const tableData = transformDataToTable(data)

  if (tableData.length === 0) {
    return <Box sx={{ padding: 2, textAlign: 'center', color: '#6A6C6D' }}>No data available</Box>
  }

  return (
    <Box sx={{ width: '100%', padding: '0 0.75rem 1rem 0.75rem' }}>
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {finalHeaders.map((header) => (
                <TableCell
                  style={{ whiteSpace: 'nowrap', width: `calc(100% / ${finalHeaders.length})` }}
                  key={header.name}
                >
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <span>{header.name}</span>
                    {header.isRequired && (
                      <span style={{ color: 'rgb(212, 53, 28)', margin: 0 }}>*</span>
                    )}
                  </span>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableData.map((row) => {
              return (
                <TableRow key={row.id}>
                  {finalHeaders.map((header) => (
                    <TableCell key={row.id + header.name}>
                      <span
                        style={{ fontWeight: 500 }}
                        dangerouslySetInnerHTML={{
                          __html: getSanitizedValue(row[header.name] || 'NA'),
                        }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </Box>
  )
}
